<script setup lang="ts">
import { ArrowRight, ChevronDown } from "lucide-vue-next";
import PublicPageLayout from "~/components/public/PublicPageLayout.vue";

const { t } = useI18n();
const localeRoute = useLocaleRoute();

const { canonicalUrl, currentLocale } = useLocalizedSeoLinks("/faq");

useSeoMeta({
  title: () => t("public_pages.faq.seo.title"),
  description: () => t("public_pages.faq.seo.description"),
  robots: "index, follow",
  ogTitle: () => t("public_pages.faq.seo.title"),
  ogDescription: () => t("public_pages.faq.seo.description"),
  ogType: "website",
  ogUrl: () => canonicalUrl.value,
  ogLocale: () => currentLocale.value.ogLocale,
  ogSiteName: "Senta.lt",
});

const sections = [
  {
    key: "clients",
    label: "public_pages.about.clients.label",
    title: "public_pages.faq.clients_title",
    items: ["find", "contact", "cost", "locations", "reviews"],
  },
  {
    key: "professionals",
    label: "public_pages.about.professionals.label",
    title: "public_pages.faq.professionals_title",
    items: ["professional", "edit_profile", "visibility", "support"],
  },
] as const;
</script>

<template>
  <PublicPageLayout
    :eyebrow="$t('footer.information.items.faq')"
    :title="$t('public_pages.faq.title')"
    :description="$t('public_pages.faq.description')"
  >
    <div class="max-w-[920px]">
      <section
        v-for="(section, sectionIndex) in sections"
        :key="section.key"
        :class="sectionIndex ? 'mt-20 sm:mt-24' : ''"
      >
        <p class="text-xs font-bold uppercase tracking-[0.13em] text-primary">
          {{ $t(section.label) }}
        </p>
        <h2 class="mt-3 text-3xl font-bold tracking-tight text-app-text">
          {{ $t(section.title) }}
        </h2>

        <div class="mt-8 divide-y divide-black/10">
          <details
            v-for="item in section.items"
            :key="item"
            class="group py-6 first:pt-0"
          >
            <summary
              class="flex cursor-pointer list-none items-center justify-between gap-8 text-base font-bold text-app-text sm:text-lg"
            >
              {{ $t(`public_pages.faq.items.${item}.question`) }}
              <ChevronDown
                class="size-4 shrink-0 text-app-muted transition group-open:rotate-180"
              />
            </summary>
            <p
              class="mt-3 max-w-3xl pr-10 text-sm leading-6 text-app-muted sm:text-base sm:leading-7"
            >
              {{ $t(`public_pages.faq.items.${item}.answer`) }}
            </p>
          </details>
        </div>
      </section>

      <div
        class="mt-20 flex flex-col gap-4 sm:mt-24 sm:flex-row sm:items-end sm:justify-between"
      >
        <div class="max-w-xl">
          <h2 class="text-xl font-bold text-app-text">
            {{ $t("public_pages.faq.more.title") }}
          </h2>
          <p class="mt-2 text-sm leading-6 text-app-muted">
            {{ $t("public_pages.faq.more.description") }}
          </p>
        </div>
        <NuxtLink
          :to="localeRoute('/contacts') || '/contacts'"
          class="inline-flex shrink-0 items-center gap-2 text-sm font-bold text-primary animated-underline"
        >
          {{ $t("public_pages.faq.more.action") }}
          <ArrowRight class="size-4" />
        </NuxtLink>
      </div>
    </div>
  </PublicPageLayout>
</template>
